import { Box, Tag, Text, Wrap, WrapItem } from '@chakra-ui/react'
import { DateTime } from 'luxon'
import type React from 'react'

import toEnumValueOrNull from '@/utils/toEnumValueOrNull'

import { LearnerFeedbackAttributeTag } from '@/client/services/learnerFeedback/constants'

import AttributeTag from '@/components/LearnerFeedback/AttributeTag'
import MessageBox from '@/components/UI/MessageBox'
import type { UserDetails } from '@/components/Worlds/types'
import AvatarWithName from '@/components/Worlds/UI/Avatar/AvatarWithName'
import Card from '@/components/Worlds/UI/Card'

export type LearnerFeedbackCardUser = Pick<
  UserDetails,
  | 'id'
  | 'displayName'
  | 'avatarUrl'
  | 'roles'
  | 'pronouns'
  | 'bio'
  | 'points'
  | 'username'
  | 'activeAnniversary'
  | 'keyClubDistrictName'
  | 'collegeInfo'
>

export default function LearnerFeedbackCard({
  user,
  isSelfReflection,
  showRecipient,
  showType,
  positionInSeries,
  createdAt,
  comment,
  tags,
}: {
  user: LearnerFeedbackCardUser
  isSelfReflection: boolean
  showRecipient: boolean
  showType: boolean
  positionInSeries: number | null
  createdAt: string
  comment: string | null
  tags: string[]
}): React.ReactElement {
  return (
    <Card px="32px" py="28px">
      <Box display="flex" alignItems="center" gap="18px" mb="12px">
        <Box display="flex" alignItems="center" gap="10px">
          <AvatarWithName user={user} size="md" distinguishSelf />
          {showType ? (
            <Tag variant="outline" px="8px" py="0px" ml="4px">
              {`${isSelfReflection ? 'Self' : 'Peer'} Reflection`}
            </Tag>
          ) : (
            <Tag variant="outline" px="8px" py="0px" ml="4px">
              Session {positionInSeries}
            </Tag>
          )}
          <Text color="fg.subtle" fontSize="sm">
            {DateTime.fromISO(createdAt).toFormat('MMM d, yyyy')}
          </Text>
        </Box>
      </Box>
      {comment != null && comment.length > 0 ? (
        <MessageBox
          text={comment}
          hidePointer={showRecipient}
          {...(isSelfReflection ? { bgColor: 'bg.success' } : {})}
        />
      ) : null}
      <Wrap spacing="16px" mt="16px" align="center" justify={{ base: 'center', md: 'flex-start' }}>
        {tags.map(tag => {
          const tagValue = toEnumValueOrNull(LearnerFeedbackAttributeTag, tag)

          return tagValue == null ? null : (
            <WrapItem key={tagValue}>
              <AttributeTag tagValue={tagValue} />
            </WrapItem>
          )
        })}
      </Wrap>
    </Card>
  )
}
